import {
  RadioUniDriver,
  TabsUniDriver,
  ThumbnailUniDriver,
  ToggleSwitchUniDriver,
} from '@wix/design-system/dist/testkit/unidriver';
import {
  PollData_Design_PollDesign_Background_Type,
  PollData_Layout_PollLayout_Direction,
  PollData_Layout_PollLayout_Type,
  PollData_Poll_Settings_Permissions_ViewRole,
} from 'ricos-schema/next';
import type { BaseUniDriver } from 'ricos-types/testkit';
import { dataHook } from 'wix-rich-content-common';
import type { FooterActionsUnidrver } from 'wix-rich-content-ui-components/testkit';
import { createFooterActionsUnidriver } from 'wix-rich-content-ui-components/testkit';

import {
  POLL_BACKGROUND_COLOR,
  POLL_BACKGROUND_GRADIENT,
  POLL_BACKGROUND_IMAGE,
  POLL_DIRECTION_LTR,
  POLL_DIRECTION_RTL,
  POLL_LAYOUT_GRID,
  POLL_LAYOUT_LIST,
  POLL_VISIBILITY_OPTION_CREATOR,
  POLL_VISIBILITY_OPTION_EVERYONE,
  POLL_VISIBILITY_OPTION_VOTERS,
  POLLS_EACH_PERSON_VOTE_TOGGLE,
  POLLS_IMAGE_TO_ANSWERS_TOGGLE,
  POLLS_IMAGE_TO_QUESTION_TOGGLE,
  POLLS_MULTIPLE_ANSWERS_TOGGLE,
  POLLS_SETTINGS_MODAL,
  POLLS_SETTINGS_TABS,
  POLLS_VOTE_COUNT_TOGGLE,
} from '../dataHooks';

export type PollsSettingsUniDriver = {
  waitForElement: () => Promise<void>;
  tabs: ReturnType<typeof TabsUniDriver>;
  layoutTab: {
    click: () => Promise<void>;
    enablePollImageToggle: ReturnType<typeof ToggleSwitchUniDriver>;
    getPollLayoutThumbnail: (
      type: PollData_Layout_PollLayout_Type
    ) => Promise<ReturnType<typeof ThumbnailUniDriver>>;
    enableOptionsImageToggle: ReturnType<typeof ToggleSwitchUniDriver>;
    getPollLayoutDirectionThumbnail: (
      type: PollData_Layout_PollLayout_Direction
    ) => Promise<ReturnType<typeof ThumbnailUniDriver>>;
  };
  designTab: {
    click: () => Promise<void>;
    getBackgroundStyleThumbnail: (
      type: PollData_Design_PollDesign_Background_Type
    ) => Promise<ReturnType<typeof ThumbnailUniDriver>>;
  };
  settingsTab: {
    click: () => Promise<void>;
    whoCanVoteRadio: (
      type: PollData_Poll_Settings_Permissions_ViewRole
    ) => ReturnType<typeof RadioUniDriver>;
    allowMultipleAnswersToggle: ReturnType<typeof ToggleSwitchUniDriver>;
    showVoteCountToggle: ReturnType<typeof ToggleSwitchUniDriver>;
    showVotedPersonToggle: ReturnType<typeof ToggleSwitchUniDriver>;
  };
} & FooterActionsUnidrver;

export const createPollsSettingsUniDriver: BaseUniDriver<PollsSettingsUniDriver> = getBase => {
  return {
    waitForElement: async () => {
      const settings = await getBase().get(dataHook(POLLS_SETTINGS_MODAL));
      await settings.wait();
    },
    tabs: TabsUniDriver(getBase().$(dataHook(POLLS_SETTINGS_TABS)), getBase()),
    layoutTab: {
      click: async () => {
        const tabs = TabsUniDriver(getBase().$(dataHook(POLLS_SETTINGS_TABS)), getBase());
        await tabs.clickTabAt(0);
      },
      enablePollImageToggle: ToggleSwitchUniDriver(
        getBase().$(dataHook(POLLS_IMAGE_TO_QUESTION_TOGGLE)),
        getBase()
      ),
      getPollLayoutThumbnail: async type => {
        const dataHookByType: Record<PollData_Layout_PollLayout_Type, string> = {
          [PollData_Layout_PollLayout_Type.LIST]: POLL_LAYOUT_LIST,
          [PollData_Layout_PollLayout_Type.GRID]: POLL_LAYOUT_GRID,
        };
        const settings = await getBase().get(dataHook(POLLS_SETTINGS_MODAL));
        const selectedLayout = await settings.get(dataHook(dataHookByType[type]));
        return ThumbnailUniDriver(selectedLayout, getBase());
      },
      enableOptionsImageToggle: ToggleSwitchUniDriver(
        getBase().$(dataHook(POLLS_IMAGE_TO_ANSWERS_TOGGLE)),
        getBase()
      ),
      getPollLayoutDirectionThumbnail: async type => {
        const dataHookByType: Record<PollData_Layout_PollLayout_Direction, string> = {
          [PollData_Layout_PollLayout_Direction.LTR]: POLL_DIRECTION_LTR,
          [PollData_Layout_PollLayout_Direction.RTL]: POLL_DIRECTION_RTL,
        };

        const settings = await getBase().get(dataHook(POLLS_SETTINGS_MODAL));
        const selectedDirection = await settings.get(dataHook(dataHookByType[type]));
        return ThumbnailUniDriver(selectedDirection, getBase());
      },
    },
    designTab: {
      click: async () => {
        const tabs = TabsUniDriver(getBase().$(dataHook(POLLS_SETTINGS_TABS)), getBase());
        await tabs.clickTabAt(1);
      },
      getBackgroundStyleThumbnail: async type => {
        const dataHookByType: Record<PollData_Design_PollDesign_Background_Type, string> = {
          [PollData_Design_PollDesign_Background_Type.COLOR]: POLL_BACKGROUND_COLOR,
          [PollData_Design_PollDesign_Background_Type.GRADIENT]: POLL_BACKGROUND_GRADIENT,
          [PollData_Design_PollDesign_Background_Type.IMAGE]: POLL_BACKGROUND_IMAGE,
        };

        const settings = await getBase().get(dataHook(POLLS_SETTINGS_MODAL));
        const selectedStyle = await settings.get(dataHook(dataHookByType[type]));
        return ThumbnailUniDriver(selectedStyle, getBase());
      },
    },
    settingsTab: {
      click: async () => {
        const tabs = TabsUniDriver(getBase().$(dataHook(POLLS_SETTINGS_TABS)), getBase());
        await tabs.clickTabAt(2);
      },
      whoCanVoteRadio: type => {
        const dataHookByType: Record<PollData_Poll_Settings_Permissions_ViewRole, string> = {
          [PollData_Poll_Settings_Permissions_ViewRole.EVERYONE]: POLL_VISIBILITY_OPTION_EVERYONE,
          [PollData_Poll_Settings_Permissions_ViewRole.VOTERS]: POLL_VISIBILITY_OPTION_VOTERS,
          [PollData_Poll_Settings_Permissions_ViewRole.CREATOR]: POLL_VISIBILITY_OPTION_CREATOR,
        };
        return RadioUniDriver(getBase().$(dataHook(dataHookByType[type])), getBase());
      },
      allowMultipleAnswersToggle: ToggleSwitchUniDriver(
        getBase().$(dataHook(POLLS_MULTIPLE_ANSWERS_TOGGLE)),
        getBase()
      ),
      showVoteCountToggle: ToggleSwitchUniDriver(
        getBase().$(dataHook(POLLS_VOTE_COUNT_TOGGLE)),
        getBase()
      ),
      showVotedPersonToggle: ToggleSwitchUniDriver(
        getBase().$(dataHook(POLLS_EACH_PERSON_VOTE_TOGGLE)),
        getBase()
      ),
    },
    ...createFooterActionsUnidriver(getBase),
  };
};
